/**
 * Telefone do cartão de contato enviado pelo chat — separado do diálogo pra ser
 * testável sem DOM.
 *
 * O identificador do WhatsApp é gravado como JID (`5547...@s.whatsapp.net`) e,
 * em conta nova, como LID (`1854...@lid`). Mandar o JID cru como telefone fazia
 * o cartão chegar no celular com `TEL:5547...@s.whatsapp.net` (TCK-2026-000072).
 *
 * O número do WhatsApp continua vindo primeiro (é ele que abre a conversa a
 * partir do cartão), mas só a parte antes do `@` e nunca um LID, que não é
 * telefone. Sai no formato internacional com `+`: é o `+55` que faz o gateway
 * marcar o cartão com o número do WhatsApp (`waid`) e deixá-lo tocável.
 */
import type { ContactIdentifiers } from '@troia-v3/types';

import { formatPhoneNumber } from '@/shared/utils';

const PHONE_JID_SUFFIX = '@s.whatsapp.net';

/** Número de um identificador do WhatsApp; `undefined` quando não é telefone (LID, grupo). */
function whatsappNumber(identifier: string): string | undefined {
  const value = identifier.trim();
  if (value.endsWith(PHONE_JID_SUFFIX)) return value.slice(0, -PHONE_JID_SUFFIX.length);
  return value.includes('@') ? undefined : value;
}

export function contactCardPhone(identifiers: Partial<ContactIdentifiers>): string | undefined {
  const candidates = [
    ...(identifiers.whatsapp ?? []).map(whatsappNumber),
    ...(identifiers.phone ?? []).map((phone) => phone.trim()),
  ];
  const raw = candidates.find((value): value is string => Boolean(value && /\d/.test(value)));
  if (!raw) return undefined;

  return formatPhoneNumber(raw) || raw;
}
